import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import api from '../../services/api';
import { Plus, Trash2 } from 'lucide-react';

const DEFAULT_STAGES = [
  { stage_id: 'technical', name: 'Technical Evaluation', order: 1, approval_required: false, approval_group: '', notify_supplier_on_reject: true },
  { stage_id: 'commercial', name: 'Commercial Evaluation', order: 2, approval_required: false, approval_group: '', notify_supplier_on_reject: true },
  { stage_id: 'final', name: 'Final Selection & Award', order: 3, approval_required: false, approval_group: '', notify_supplier_on_reject: true },
];

export function EvaluationStagesTab({ stages, setStages }) {
  const [approvalGroups, setApprovalGroups] = useState([]);

  useEffect(() => {
    if (!stages || stages.length === 0) setStages(DEFAULT_STAGES);
    api.get('/api/admin/approval-groups').then(r => setApprovalGroups(r.data?.groups || r.data?.data || [])).catch(() => {});
  }, []); // eslint-disable-line

  const updateStage = (idx, field, value) => {
    const updated = [...stages];
    updated[idx] = { ...updated[idx], [field]: value };
    setStages(updated);
  };

  const addStage = () => {
    setStages([...stages, {
      stage_id: `stage_${Date.now()}`, name: '', order: stages.length + 1,
      approval_required: false, approval_group: '', notify_supplier_on_reject: true,
    }]);
  };

  const removeStage = (idx) => setStages(stages.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4 py-2" data-testid="stages-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Evaluation Stages</h3>
          <p className="text-xs text-slate-500 mt-0.5">Define the stages supplier responses pass through during evaluation. Each stage can optionally require approval from an approval group.</p>
        </div>
        <Button size="sm" onClick={addStage} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-stage-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Stage
        </Button>
      </div>

      {(stages || []).map((stage, idx) => (
        <div key={stage.stage_id || idx} className="border rounded-lg p-4 bg-white" data-testid={`stage-${idx}`}>
          <div className="flex items-start gap-4">
            <div className="bg-indigo-100 text-indigo-700 rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">
              {idx + 1}
            </div>
            <div className="flex-1 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Stage Name</Label>
                  <Input value={stage.name} onChange={e => updateStage(idx, 'name', e.target.value)}
                    placeholder="e.g. Technical Evaluation" className="h-8 text-sm" data-testid={`stage-name-${idx}`} />
                </div>
                <div>
                  <Label className="text-xs">Approval Group (optional)</Label>
                  <Select value={stage.approval_group || '__none__'} onValueChange={v => updateStage(idx, 'approval_group', v === '__none__' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`stage-approval-${idx}`}><SelectValue placeholder="No approval required" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No approval required</SelectItem>
                      {approvalGroups.map(g => (
                        <SelectItem key={g.group_code || g.name} value={g.group_code || g.name}>{g.name || g.group_code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <Checkbox checked={stage.approval_required} onCheckedChange={c => updateStage(idx, 'approval_required', c)} />
                  <Label className="text-xs">Require approval to advance</Label>
                </div>
                <div className="flex items-center gap-1.5">
                  <Checkbox checked={stage.notify_supplier_on_reject} onCheckedChange={c => updateStage(idx, 'notify_supplier_on_reject', c)} />
                  <Label className="text-xs">Notify supplier if rejected at this stage</Label>
                </div>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={() => removeStage(idx)} className="text-red-400 hover:text-red-600 h-7 px-2">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
